import type {SafeHTMLString} from '@github-ui/safe-html'
import type {Comment} from '@github-ui/diff-lines/conversation-types'
import {VALUES} from '@github-ui/commenting/Values'

/**
 * Per-item `bodyHTML` cap. Covers nearly every review comment while keeping the
 * worst case (~1.6 MB at the 50-item ceiling) well under sessionStorage's ~5 MB
 * budget. Applied here, not in storage, so in-memory and reloaded items render
 * identically.
 */
export const COMMENT_SNAPSHOT_BODY_CAP_BYTES = 32 * 1024

export type CommentSnapshotAuthor = {login: string; avatarUrl?: string}

/** Strips a trailing `[bot]` marker from a bot login, mirroring the thread header. */
function formatBotAuthorLogin(login: string): string {
  return login.replace(/\[bot\]$/i, '')
}

/**
 * Resolves the snapshot's display author to match the source thread, mirroring
 * `AutomatedReviewThreadComment`: Copilot comments show `Copilot`, other bots
 * have their `[bot]` marker stripped, and a missing author falls back to ghost.
 */
function resolveSnapshotAuthor(comment: CommentSnapshotInput): CommentSnapshotAuthor {
  if (comment.automatedComment) {
    const login = comment.automatedComment.aiAuthored
      ? 'Copilot'
      : comment.author
        ? formatBotAuthorLogin(comment.author.login)
        : VALUES.ghostUser.login
    return {login, avatarUrl: comment.author?.avatarUrl}
  }
  return comment.author
    ? {login: comment.author.login, avatarUrl: comment.author.avatarUrl}
    : {login: VALUES.ghostUser.login}
}

export type CommentSnapshot = {
  author: CommentSnapshotAuthor
  createdAt: string
  bodyHTML: SafeHTMLString
  bodyTruncated: boolean
}

/** Snapshot for `human-with-suggestion` items, which also carry the extracted blob HTML. */
export type CommentSnapshotWithSuggestion = CommentSnapshot & {suggestionBlobHTML: SafeHTMLString}

/** The subset of a {@link Comment} that {@link buildCommentSnapshot} reads. */
export type CommentSnapshotInput = Pick<Comment, 'author' | 'createdAt' | 'bodyHTML' | 'automatedComment'>

export type BuildCommentSnapshotOptions = {
  /**
   * When provided, the returned snapshot includes `suggestionBlobHTML` extracted
   * from the `div.js-suggested-changes-blob` at this index within the comment's
   * server-rendered body. Used for `human-with-suggestion` items.
   */
  suggestionIndex?: number
}

export function buildCommentSnapshot(
  comment: CommentSnapshotInput,
  opts: {suggestionIndex: number},
): CommentSnapshotWithSuggestion
export function buildCommentSnapshot(comment: CommentSnapshotInput, opts?: BuildCommentSnapshotOptions): CommentSnapshot
/**
 * Builds the snapshot the Manage batch panel renders in-place, centralizing the
 * body source, ghost-author fallback, size cap, and the single `SafeHTMLString`
 * ingress cast.
 *
 * Body source is `automatedComment.message` for CCR (its `bodyHTML` is only a
 * summary line — see `AutomatedReviewThreadComment`), else `comment.bodyHTML`;
 * both are server-sanitized, so the cast here is the correct ingress point.
 * Over-cap bodies become `''` with `bodyTruncated` set (never clipped mid-tag,
 * which wouldn't be sanitization-safe).
 *
 * With `opts.suggestionIndex`, also carries `suggestionBlobHTML` (extraction
 * wired in a follow-up; empty for now to satisfy the required shape).
 */
export function buildCommentSnapshot(
  comment: CommentSnapshotInput,
  opts: BuildCommentSnapshotOptions = {},
): CommentSnapshot | CommentSnapshotWithSuggestion {
  const author = resolveSnapshotAuthor(comment)

  const rawBody = comment.automatedComment?.message ?? comment.bodyHTML ?? ''
  const bodyTruncated = byteLength(rawBody) > COMMENT_SNAPSHOT_BODY_CAP_BYTES
  // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- automatedComment.message / bodyHTML are sanitized server-side; this is the data-ingress assertion point.
  const bodyHTML = (bodyTruncated ? '' : rawBody) as SafeHTMLString

  const snapshot: CommentSnapshot = {author, createdAt: comment.createdAt, bodyHTML, bodyTruncated}

  if (opts.suggestionIndex !== undefined) {
    // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Derived from the server-sanitized bodyHTML; ingress assertion point.
    const suggestionBlobHTML = '' as SafeHTMLString
    return {...snapshot, suggestionBlobHTML}
  }

  return snapshot
}

const textEncoder = new TextEncoder()

function byteLength(value: string): number {
  return textEncoder.encode(value).byteLength
}
